import React, { useState, useEffect, lazy, Suspense, startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import { useDataCache } from '../contexts/DataCacheContext';
import DashboardLayout from './DashboardLayout';

const CallLogs = lazy(() => import('./CallLogs'));
const SMSConversations = lazy(() => import('./SMSConversations'));

function Logs() {
  const location = useLocation();
  const { cache, prefetchLogs } = useDataCache();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('call-logs');
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSection, setExpandedSection] = useState('call-logs');

  useEffect(() => {
    if (!cache.logs) {
      prefetchLogs();
    }
  }, []);

  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Set active tab based on URL
    const path = location.pathname;
    if (path.includes('/sms')) setActiveTab('sms-conversations');
    else setActiveTab('call-logs');
  }, [location.pathname]);

  const getCurrentSubPage = () => {
    const subPages = {
      'call-logs': { icon: 'fa-phone', label: 'Call Logs' },
      'sms-conversations': { icon: 'fa-comments', label: 'SMS' }
    };
    return subPages[activeTab] || subPages['call-logs'];
  };

  return (
    <DashboardLayout currentSubPage={getCurrentSubPage()}>
      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sub Menu */}
        <div className="w-full md:w-64 md:flex-shrink-0 md:mr-4">
          <div className="h-full p-2" style={{background: '#F5F5F7'}}>
            <nav className="space-y-1 flex flex-col md:flex-col overflow-x-visible">
              
              {/* Call Logs */}
              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (window.innerWidth < 768) {
                        setExpandedSection(expandedSection === 'call-logs' ? null : 'call-logs');
                      } else {
                        setActiveTab('call-logs');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (window.innerWidth < 768 ? expandedSection === 'call-logs' : activeTab === 'call-logs')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-phone text-sm`} style={(window.innerWidth < 768 ? expandedSection === 'call-logs' : activeTab === 'call-logs') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">Call Logs</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'call-logs' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'call-logs' && window.innerWidth < 768 && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">Call Logs</div>
                          <p className="text-sm text-gray-600">View and analyze your assistant's call history and performance.</p>
                        </div>
                        <CallLogs cachedConversations={cache.logs?.conversations} cachedLastKey={cache.logs?.last_key} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SMS */}
              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (window.innerWidth < 768) {
                        setExpandedSection(expandedSection === 'sms-conversations' ? null : 'sms-conversations');
                      } else {
                        setActiveTab('sms-conversations');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (window.innerWidth < 768 ? expandedSection === 'sms-conversations' : activeTab === 'sms-conversations')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-comments text-sm`} style={(window.innerWidth < 768 ? expandedSection === 'sms-conversations' : activeTab === 'sms-conversations') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">SMS</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'sms-conversations' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'sms-conversations' && window.innerWidth < 768 && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">SMS Conversations</div>
                          <p className="text-sm text-gray-600">Manage and view SMS conversations with your customers.</p>
                        </div>
                        <SMSConversations cachedSmsConversations={cache.logs?.smsConversations} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>

        {/* Main Content - Desktop Only */}
        <div className="hidden md:block flex-1 min-w-0">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          }>
            {activeTab === 'call-logs' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">Call Logs</div>
                    <p className="text-sm text-gray-600">View and analyze your assistant's call history and performance.</p>
                  </div>
                  <CallLogs cachedConversations={cache.logs?.conversations} cachedLastKey={cache.logs?.last_key} />
                </div>
              )}

              {activeTab === 'sms-conversations' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">SMS Conversations</div>
                    <p className="text-sm text-gray-600">Manage and view SMS conversations with your customers.</p>
                  </div>
                  <SMSConversations cachedSmsConversations={cache.logs?.smsConversations} />
                </div>
              )}
            </Suspense>
          </div>
      </div>
    </DashboardLayout>
  );
}

export default Logs;